import { useRef, useState, type FormEvent } from 'react';
import {
  emptyEnquiry,
  enquiryFingerprint,
  EnquiryNotConfiguredError,
  submitEnquiry,
  validateEnquiry,
  validateField,
  type EnquiryErrors,
  type EnquiryField,
  type EnquiryValues,
} from '../utils/enquiry';

export type SubmitState =
  | { status: 'idle' }
  | { status: 'submitting' }
  | { status: 'success' }
  | { status: 'error'; kind: 'not-configured' | 'network' | 'spam' | 'duplicate'; message: string };

const SENT_KEY = 'liftoff-enquiry-last-sent';
const MIN_FILL_MS = 3000;

export function useEnquiryForm(initial: Partial<EnquiryValues>, relatedProject?: string) {
  const [values, setValues] = useState<EnquiryValues>({ ...emptyEnquiry, ...initial });
  const [errors, setErrors] = useState<EnquiryErrors>({});
  const [state, setState] = useState<SubmitState>({ status: 'idle' });
  const mountedAt = useRef(Date.now());
  const inFlight = useRef(false);

  const setField = <K extends EnquiryField>(name: K, value: EnquiryValues[K]) => {
    setValues((current) => {
      const next = { ...current, [name]: value };
      if (errors[name]) setErrors((prev) => ({ ...prev, [name]: validateField(name, next) }));
      return next;
    });
    if (state.status === 'error') setState({ status: 'idle' });
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (inFlight.current) return;

    const nextErrors = validateEnquiry(values);
    setErrors(nextErrors);
    const firstInvalid = Object.keys(nextErrors)[0];
    if (firstInvalid) {
      document.getElementById(`enquiry-${firstInvalid}`)?.focus();
      return;
    }

    if (values.website) {
      setState({ status: 'error', kind: 'spam', message: 'Your enquiry could not be sent. Please contact us by phone or email.' });
      return;
    }
    if (Date.now() - mountedAt.current < MIN_FILL_MS) {
      setState({
        status: 'error',
        kind: 'spam',
        message: 'That was submitted very quickly. Please check your details and submit again.',
      });
      return;
    }

    const fingerprint = enquiryFingerprint(values);
    if (window.sessionStorage.getItem(SENT_KEY) === fingerprint) {
      setState({
        status: 'error',
        kind: 'duplicate',
        message: 'This browser has already sent this enquiry, so we have not sent it again.',
      });
      return;
    }

    inFlight.current = true;
    setState({ status: 'submitting' });
    try {
      await submitEnquiry(values, relatedProject);
      window.sessionStorage.setItem(SENT_KEY, fingerprint);
      setState({ status: 'success' });
    } catch (error) {
      if (error instanceof EnquiryNotConfiguredError) {
        setState({ status: 'error', kind: 'not-configured', message: error.message });
      } else {
        setState({
          status: 'error',
          kind: 'network',
          message: error instanceof Error ? error.message : 'Something went wrong while sending your enquiry.',
        });
      }
    } finally {
      inFlight.current = false;
    }
  };

  const reset = () => {
    setValues({ ...emptyEnquiry });
    setErrors({});
    setState({ status: 'idle' });
    mountedAt.current = Date.now();
  };

  return { values, errors, state, setField, handleSubmit, reset };
}
